import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  Building2, Users, FileText, ClipboardList, TrendingUp, CheckCircle2, Plus, RefreshCw,
  IndianRupee, Clock, AlertTriangle, Sparkles, ArrowUpRight,
  LifeBuoy, Crosshair, Activity, UserCheck,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer,
} from 'recharts';
import * as apiClient from '@/services/apiClient';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import CreateCompanyWizard, { CredentialsBanner } from '@/components/superadmin/CreateCompanyWizard';
import CompaniesTableSection from '@/components/superadmin/CompaniesTableSection';
import TopTenantsBarList from '@/components/superadmin/TopTenantsBarList';
import MonthlyCompanyChart from '@/components/superadmin/MonthlyCompanyChart';
import { sanitizeMonthlyCompanyCounts, toChartNumber } from '@/lib/chartUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const CURRENCY = '₹';

const PLAN_COLORS = {
  starter:      { dot: 'bg-slate-400',  hex: '#94a3b8' },
  professional: { dot: 'bg-blue-400',   hex: '#60a5fa' },
  growth:       { dot: 'bg-teal-400',   hex: '#2dd4bf' },
  scale:        { dot: 'bg-purple-400', hex: '#c084fc' },
};
const PLAN_COLORS_DOT = Object.fromEntries(Object.entries(PLAN_COLORS).map(([k, v]) => [k, v.dot]));

function formatMRR(value) {
  if (value >= 100000) return `${CURRENCY}${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${CURRENCY}${(value / 1000).toFixed(1)}K`;
  return `${CURRENCY}${value}`;
}

// ── Scoped dashboard for echohire_staff ───────────────────────────────────────
// Only fetches and renders widgets for sections the user has permission to see.

const SCOPED_WIDGETS = [
  {
    key: 'sa_companies',
    label: 'Companies',
    icon: Building2,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    fetch: (api) => api.getOverallStats(),
    render: (data) => {
      const s = data?.data ?? data;
      return [
        { label: 'Total Companies', value: s?.totalCompanies ?? '—' },
        // "Active" used to mean "not manually deactivated", which included
        // expired and trial accounts and read as though they were paying.
        { label: 'Paying', value: s?.payingCompanies ?? '—' },
        { label: 'With Access', value: s?.activeCompanies ?? '—' },
        { label: 'Total Users', value: s?.totalUsers ?? '—' },
      ];
    },
  },
  {
    key: 'sa_leads',
    label: 'Leads',
    icon: UserCheck,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    fetch: (api) => api.getLeadStats?.() || Promise.resolve(null),
    render: (data) => {
      const s = data?.data ?? data;
      return [
        { label: 'Total Leads', value: s?.total ?? '—' },
        { label: 'New This Week', value: s?.newThisWeek ?? '—' },
        { label: 'Demo Scheduled', value: s?.demoScheduled ?? '—' },
      ];
    },
  },
  {
    key: 'sa_earnings',
    label: 'Earnings',
    icon: IndianRupee,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    fetch: (api) => api.getOverallStats(),
    render: (data) => {
      const s = data?.data ?? data;
      return [
        { label: 'MRR', value: s?.mrr != null ? formatMRR(s.mrr) : '—' },
        // Money actually received, from recorded payments. The old field name
        // was never returned by the API, so this rendered blank.
        { label: 'Collected', value: s?.totalCollected != null ? formatMRR(s.totalCollected) : '—' },
        { label: 'Outstanding', value: s?.billing?.totalOutstanding != null ? formatMRR(s.billing.totalOutstanding) : '—' },
      ];
    },
  },
  {
    key: 'sa_trials',
    label: 'Trials',
    icon: Clock,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    fetch: (api) => api.getOverallStats(),
    render: (data) => {
      const s = data?.data ?? data;
      return [
        { label: 'Active Trials', value: s?.trialCompanies ?? '—' },
        { label: 'Needs Review', value: s?.needsReview ?? '—' },
        {
          label: 'Converted',
          value: s?.trialConverted != null && s?.trialStarted
            ? `${s.trialConverted} / ${s.trialStarted}`
            : (s?.trialConverted ?? '—'),
        },
      ];
    },
  },
  {
    key: 'sa_prospects',
    label: 'Prospects',
    icon: Crosshair,
    color: 'text-rose-500',
    bg: 'bg-rose-500/10',
    fetch: (api) => api.getProspectLeadStats?.() || Promise.resolve(null),
    render: (data) => {
      const s = data?.data ?? data;
      return [
        { label: 'Total Prospects', value: s?.total ?? '—' },
        { label: 'Active Drip', value: s?.activeDrip ?? '—' },
      ];
    },
  },
  {
    key: 'sa_support',
    label: 'Support',
    icon: LifeBuoy,
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
    fetch: (api) => api.getHealthKpis?.() || Promise.resolve(null),
    render: (data) => {
      const s = data?.data ?? data;
      return [
        { label: 'Open Tickets', value: s?.support?.openTickets ?? '—' },
      ];
    },
  },
  {
    key: 'sa_health',
    label: 'System Health',
    icon: Activity,
    color: 'text-teal-500',
    bg: 'bg-teal-500/10',
    fetch: (api) => api.getHealthSystem?.() || Promise.resolve(null),
    render: (data) => {
      const s = data?.data ?? data;
      const diskPct = s?.disk?.usedPercent;
      const memPct  = s?.memory?.usedPercent;
      return [
        { label: 'Disk Usage', value: diskPct != null ? `${diskPct}%` : '—' },
        { label: 'RAM Usage',  value: memPct  != null ? `${memPct}%`  : '—' },
        { label: 'Uptime',     value: s?.server?.nodeUptime != null ? `${Math.floor(s.server.nodeUptime / 3600)}h` : '—' },
      ];
    },
  },
];

function ScopedDashboard({ user }) {
  const perms = Array.isArray(user?.saPermissions) ? user.saPermissions : [];
  const widgets = SCOPED_WIDGETS.filter(w => perms.includes(w.key));
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const api = { ...apiClient };
    Promise.allSettled(widgets.map(w => w.fetch(api).then(d => [w.key, d]))).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(r => { if (r.status === 'fulfilled') map[r.value[0]] = r.value[1]; });
      setWidgetData(map);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [perms.join(',')]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {widgets.map(w => <div key={w.key} className="h-36 animate-pulse rounded-xl bg-muted" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Your Dashboard</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Summary of your assigned sections</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {widgets.map(w => {
          const Icon = w.icon;
          const rows = w.render(widgetData[w.key]);
          return (
            <div key={w.key} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2.5 mb-4">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${w.bg}`}>
                  <Icon className={`h-4 w-4 ${w.color}`} />
                </div>
                <span className="text-sm font-semibold text-foreground">{w.label}</span>
              </div>
              <div className="space-y-2">
                {rows.map(row => (
                  <div key={row.label} className="flex items-center justify-between border-b border-border/50 pb-1.5 last:border-0 last:pb-0">
                    <span className="text-xs text-muted-foreground">{row.label}</span>
                    <span className="text-sm font-bold text-foreground">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FullDashboard() {
  const [companies, setCompanies] = useState([]);
  const [overallStats, setOverallStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [companiesRes, statsRes] = await Promise.all([
        apiClient.getAllCompanies(),
        apiClient.getOverallStats(),
      ]);
      setCompanies(Array.isArray(companiesRes?.data) ? companiesRes.data : []);
      const statsPayload = statsRes?.data ?? statsRes;
      setOverallStats(
        statsPayload && typeof statsPayload === 'object' && statsPayload.totalCompanies != null
          ? statsPayload : null
      );
    } catch (err) {
      setError(err.message || 'Failed to load platform data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = useMemo(() => {
    if (!overallStats) return null;
    const inactive = overallStats.totalCompanies - overallStats.activeCompanies;
    const completionRate = overallStats.totalSessions > 0
      ? Math.round((overallStats.totalReports / overallStats.totalSessions) * 100) : 0;
    const activeRate = overallStats.totalCompanies > 0
      ? Math.round((overallStats.activeCompanies / overallStats.totalCompanies) * 100) : 0;
    const avgUsers = overallStats.totalCompanies > 0
      ? (overallStats.totalUsers / overallStats.totalCompanies).toFixed(1) : '0';
    return { inactive, completionRate, activeRate, avgUsers };
  }, [overallStats]);

  const monthlyChartData = useMemo(
    () => sanitizeMonthlyCompanyCounts(overallStats?.monthlyCompanyCounts),
    [overallStats]
  );

  const topCompaniesBySessions = useMemo(() => {
    return [...companies]
      .sort((a, b) => toChartNumber(b.statistics?.totalSessions) - toChartNumber(a.statistics?.totalSessions))
      .slice(0, 6)
      .map((c, i) => {
        const label = c.companyName?.trim() || `Company ${i + 1}`;
        return {
          name: label.length > 14 ? `${label.slice(0, 12)}…` : label,
          sessions: toChartNumber(c.statistics?.totalSessions),
          users: toChartNumber(c.statistics?.totalUsers),
        };
      });
  }, [companies]);

  const planDist = overallStats?.planDistribution || {};
  const totalPlanCompanies = Object.values(planDist).reduce((s, v) => s + v, 0);

  const handleCreated = (payload) => { setCredentials(payload); fetchData(); };

  if (loading && !overallStats) {
    return (
      <div className="mx-auto space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}
        </div>
        <Skeleton className="h-64 rounded-md" />
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-4 pb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Platform control</p>
          <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Super admin dashboard</h1>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">Tenants, revenue, and onboarding — all in one view</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={fetchData} disabled={loading}>
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
          </Button>
          <Button size="sm" className="h-9 gap-1.5 shadow-sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> New company
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <span>{error}</span>
          <button type="button" className="text-xs font-medium underline" onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      <CredentialsBanner credentials={credentials} onDismiss={() => setCredentials(null)} />

      {/* Revenue KPIs */}
      {overallStats && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground/60">Revenue & Subscriptions</h2>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <DashboardKpiCard
              title="Monthly Revenue (MRR)"
              value={formatMRR(overallStats.mrr || 0)}
              subtitle="Active subscriptions only"
              icon={IndianRupee}
              tone="success"
            />
            <DashboardKpiCard
              title="Trial Companies"
              value={overallStats.trialCompanies ?? 0}
              subtitle="14-day free trial"
              icon={Clock}
              tone="primary"
            />
            <DashboardKpiCard
              title="Grace Period"
              value={overallStats.gracePeriodCompanies ?? 0}
              subtitle={overallStats.gracePeriodCompanies > 0 ? 'Need attention!' : 'All clear'}
              icon={AlertTriangle}
              tone={overallStats.gracePeriodCompanies > 0 ? 'warning' : 'default'}
            />
            <DashboardKpiCard
              title="Expired"
              value={overallStats.expiredCompanies ?? 0}
              subtitle="Blocked accounts"
              icon={CheckCircle2}
              tone={overallStats.expiredCompanies > 0 ? 'destructive' : 'default'}
            />
          </div>
        </section>
      )}

      {/* Platform KPIs */}
      {kpis && overallStats && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground/60">Platform KPIs</h2>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
            <DashboardKpiCard title="Companies" value={overallStats.totalCompanies} subtitle={`${overallStats.activeCompanies} active`} icon={Building2} tone="primary" />
            <DashboardKpiCard title="Users" value={overallStats.totalUsers} subtitle={`${kpis.avgUsers} avg / co.`} icon={Users} tone="default" />
            <DashboardKpiCard title="Templates" value={overallStats.totalTemplates} icon={FileText} tone="default" />
            <DashboardKpiCard title="Sessions" value={overallStats.totalSessions} icon={ClipboardList} tone="warning" />
            <DashboardKpiCard title="Reports" value={overallStats.totalReports} subtitle={`${kpis.completionRate}% completion`} icon={TrendingUp} tone="success" />
            <DashboardKpiCard title="Active rate" value={`${kpis.activeRate}%`} subtitle={`${kpis.inactive} inactive`} icon={CheckCircle2} tone="success" />
          </div>
        </section>
      )}

      {/* ── Charts row — all three cards stretch to same height ── */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12 xl:items-stretch">
        {/* New companies */}
        <Card className="flex flex-col rounded-md border-border shadow-sm xl:col-span-5">
          <CardHeader className="shrink-0 space-y-0 border-b border-border/60 py-3">
            <CardTitle className="text-sm font-semibold">New companies</CardTitle>
            <CardDescription className="text-xs">Last 6 months</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col p-3 pt-2">
            <MonthlyCompanyChart rows={monthlyChartData} className="flex-1 min-h-[160px]" />
          </CardContent>
        </Card>

        {/* Top tenants */}
        <Card className="flex flex-col rounded-md border-border shadow-sm xl:col-span-4">
          <CardHeader className="shrink-0 space-y-0 border-b border-border/60 py-3">
            <CardTitle className="text-sm font-semibold">Top tenants</CardTitle>
            <CardDescription className="text-xs">By interview sessions</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col p-3 pt-2">
            <TopTenantsBarList rows={topCompaniesBySessions} className="flex-1 min-h-[160px]" />
          </CardContent>
        </Card>

        {/* Plan Distribution — compact: pie fills space, legend at bottom, button in header */}
        <Card className="flex flex-col rounded-md border-border shadow-sm xl:col-span-3">
          <CardHeader className="shrink-0 border-b border-border/60 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="space-y-0">
                <CardTitle className="text-sm font-semibold">Plan Distribution</CardTitle>
                <CardDescription className="text-xs">{totalPlanCompanies} companies</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs shrink-0" onClick={() => setCreateOpen(true)}>
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col p-3 pt-2">
            {Object.entries(planDist).length === 0 ? (
              <div className="flex flex-1 items-center justify-center">
                <p className="text-xs text-muted-foreground">No plan data yet.</p>
              </div>
            ) : (
              <>
                {/* PieChart fills remaining space */}
                <div className="flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Object.entries(planDist).map(([slug, count]) => ({
                          name: slug.charAt(0).toUpperCase() + slug.slice(1),
                          value: count,
                          slug,
                        }))}
                        cx="50%" cy="50%"
                        innerRadius="38%" outerRadius="60%"
                        paddingAngle={3} dataKey="value" isAnimationActive
                      >
                        {Object.entries(planDist).map(([slug]) => (
                          <Cell key={slug} fill={PLAN_COLORS[slug]?.hex || '#6b7280'}
                            stroke="hsl(var(--background))" strokeWidth={2} />
                        ))}
                      </Pie>
                      <ReTooltip
                        wrapperStyle={{ outline: 'none' }}
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '11px',
                          color: 'hsl(var(--foreground))',
                        }}
                        itemStyle={{ color: 'hsl(var(--foreground))' }}
                        labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                        formatter={(v, n) => [`${v} companies`, n]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Legend — always at bottom */}
                <div className="shrink-0 space-y-1.5 border-t border-border/50 pt-2 mt-2">
                  {Object.entries(planDist).map(([slug, count]) => {
                    const p = totalPlanCompanies > 0 ? Math.round((count / totalPlanCompanies) * 100) : 0;
                    return (
                      <div key={slug} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PLAN_COLORS[slug]?.hex || '#6b7280' }} />
                          <span className="capitalize text-foreground/70">{slug}</span>
                        </div>
                        <span className="font-mono font-semibold tabular-nums">
                          {count} <span className="font-normal text-foreground/55">({p}%)</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Revenue trend + Insights ── */}
      {overallStats && (
        <div className="grid gap-3 xl:grid-cols-3">
          {/* Revenue AreaChart */}
          <Card className="rounded-md border-border shadow-sm xl:col-span-2">
            <CardHeader className="space-y-0 border-b border-border/60 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-semibold">Revenue Trend</CardTitle>
                  <CardDescription className="text-xs">MRR over the last 6 months</CardDescription>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  <ArrowUpRight className="h-3.5 w-3.5" />
                  {formatMRR(overallStats.mrr || 0)} MRR
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-2">
              {(overallStats.monthlyRevenueTrend || []).length === 0 ? (
                <div className="flex h-40 items-center justify-center text-xs text-muted-foreground">
                  No revenue trend data yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={overallStats.monthlyRevenueTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="saRevGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 10 }} axisLine={false} tickLine={false}
                      tickFormatter={v => formatMRR(v)} width={56} />
                    <ReTooltip
                      contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '11px', color: 'hsl(var(--foreground))' }}
                      itemStyle={{ color: 'hsl(var(--foreground))' }}
                      labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                      formatter={v => [formatMRR(v), 'Revenue']}
                    />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#8b5cf6" strokeWidth={2.5}
                      fill="url(#saRevGrad)" dot={{ fill: '#8b5cf6', r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Insights strip */}
          <Card className="rounded-md border-border shadow-sm">
            <CardHeader className="space-y-0 border-b border-border/60 py-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" />Platform Insights
              </CardTitle>
              <CardDescription className="text-xs">Key health indicators</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 p-3">
              {[
                {
                  label: 'Trial → Active rate',
                  value: overallStats.totalCompanies > 0
                    ? `${Math.round((overallStats.activeCompanies / overallStats.totalCompanies) * 100)}%`
                    : '—',
                  hint: `${overallStats.activeCompanies} of ${overallStats.totalCompanies} converted`,
                },
                {
                  label: 'Avg interviews / company',
                  value: overallStats.activeCompanies > 0
                    ? Math.round(overallStats.totalSessions / overallStats.activeCompanies)
                    : '—',
                  hint: `${overallStats.totalSessions} total sessions`,
                },
                {
                  label: 'Report completion rate',
                  value: overallStats.totalSessions > 0
                    ? `${Math.round((overallStats.totalReports / overallStats.totalSessions) * 100)}%`
                    : '—',
                  hint: `${overallStats.totalReports} reports generated`,
                },
                {
                  label: 'At-risk accounts',
                  value: (overallStats.gracePeriodCompanies || 0) + (overallStats.expiredCompanies || 0),
                  hint: `${overallStats.gracePeriodCompanies || 0} grace · ${overallStats.expiredCompanies || 0} expired`,
                  danger: ((overallStats.gracePeriodCompanies || 0) + (overallStats.expiredCompanies || 0)) > 0,
                },
              ].map(({ label, value, hint, danger }) => (
                <div key={label} className={cn(
                  'flex items-center justify-between rounded-md border px-2.5 py-2 text-xs',
                  danger ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-muted/15'
                )}>
                  <div>
                    <p className={cn('font-medium', danger ? 'text-destructive' : 'text-foreground')}>{label}</p>
                    <p className="text-[10px] text-foreground/60">{hint}</p>
                  </div>
                  <span className={cn('font-mono text-lg font-black tabular-nums', danger ? 'text-destructive' : 'text-foreground')}>
                    {value}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <CompaniesTableSection
        companies={companies}
        pageSize={5}
        title="Companies"
        showViewAllLink
      />

      <CreateCompanyWizard open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreated} />
    </div>
  );
}

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  if (user?.role === 'echohire_staff') return <ScopedDashboard user={user} />;
  return <FullDashboard />;
}
